// What is currently selected, shown beside the map.
//
// One unit gets the full treatment — portrait, who owns it, how much health is
// left. Several units get a row of portraits with a bar under each, which is
// all there is room to say about a group and all a player needs from it.
import React from "react";
import {
  sheetFor,
  playerColour,
  playerTint,
  SHEET_COLUMNS,
  SHEET_ROWS,
  PORTRAIT_COLUMN,
  PORTRAIT_ROW,
} from "../sprites/Sprite";
import { healthColour } from "../effects/HealthBar";
import { MAX_HP } from "../../constants/GameConstants";

const PANEL_WIDTH = 208;
const SINGLE_PORTRAIT = 96;
const GROUP_PORTRAIT = 52;

// One cell of the sprite sheet, sliced out with background-position rather
// than drawn. The sheet is already loaded for the canvas, so this costs no
// extra request.
//
// The cell is drawn larger than the frame and centred, because the art leaves
// a wide transparent margin inside each 256px cell — at 1:1 the cannon ends up
// a small object adrift in a big box.
const ZOOM = 1.5;

const Portrait = ({ playerId, size }) => {
  const cell = size * ZOOM;
  const centre = (size - cell) / 2;
  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flex: "none",
        overflow: "hidden",
        backgroundImage: `url(${sheetFor(playerId)})`,
        backgroundSize: `${cell * SHEET_COLUMNS}px ${cell * SHEET_ROWS}px`,
        backgroundPosition:
          `${centre - PORTRAIT_COLUMN * cell}px ${centre - PORTRAIT_ROW * cell}px`,
        backgroundRepeat: "no-repeat",
        // The art is transparent, so this sits behind the sprite rather than
        // over it, and stays faint enough not to tint the unit itself.
        backgroundColor: playerTint(playerId),
        border: `1px solid ${playerColour(playerId)}`,
        borderRadius: 2,
      }}
    />
  );
};

const HealthBar = ({ hp, width }) => {
  const fraction = Math.max(0, Math.min(1, hp / MAX_HP));
  return (
    <div
      style={{
        width,
        height: 6,
        background: "rgba(60, 26, 24, 0.9)",
        border: "1px solid rgba(18, 18, 16, 0.78)",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${fraction * 100}%`,
          height: "100%",
          background: healthColour(fraction),
        }}
      />
    </div>
  );
};

const Label = ({ children }) => (
  <div
    style={{
      fontSize: 10,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      opacity: 0.6,
    }}
  >
    {children}
  </div>
);

const SelectionPanel = ({ selection, players, userPlayerId, nameFor }) => {
  const { owner, unitIds } = selection;
  const units = owner === null ? {} : players[owner]?.units ?? {};
  // A unit can die while it is selected. Dropping the dead here rather than
  // clearing the selection keeps the rest of a group on screen.
  const living = unitIds.filter((unitId) => units[unitId]?.alive !== false);
  const isOwn = owner === userPlayerId;

  const frame = {
    width: PANEL_WIDTH,
    flex: "none",
    padding: 12,
    boxSizing: "border-box",
    border: "1px solid rgba(0, 0, 0, 0.18)",
    borderRadius: 3,
    background: "rgba(0, 0, 0, 0.03)",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minHeight: 168,
  };

  if (owner === null || living.length === 0) {
    return (
      <div style={frame}>
        <Label>Selection</Label>
        <div style={{ fontSize: 13, opacity: 0.6, lineHeight: 1.45 }}>
          Nothing selected. Click a unit to see it, or drag a box over your own.
        </div>
      </div>
    );
  }

  const ownerName = nameFor(owner);

  if (living.length === 1) {
    const unitId = living[0];
    const hp = units[unitId]?.hp ?? MAX_HP;
    return (
      <div style={frame}>
        <Label>{isOwn ? "Selected" : "Enemy unit"}</Label>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <Portrait playerId={owner} size={SINGLE_PORTRAIT} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Bombard cannon</div>
            <div
              style={{
                fontSize: 12,
                color: playerColour(owner),
                fontWeight: 600,
                overflowWrap: "anywhere",
              }}
            >
              {ownerName}
            </div>
            <div style={{ fontSize: 12, opacity: 0.75 }}>
              {hp} / {MAX_HP} HP
            </div>
            <HealthBar hp={hp} width={72} />
          </div>
        </div>
        {!isOwn && (
          <div style={{ fontSize: 11, opacity: 0.6, lineHeight: 1.4 }}>
            Not yours — you can look, but not give it orders.
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={frame}>
      <Label>
        {isOwn ? `Selected · ${living.length}` : `Enemy units · ${living.length}`}
      </Label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {living.map((unitId) => (
          <div
            key={unitId}
            style={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            <Portrait playerId={owner} size={GROUP_PORTRAIT} />
            <HealthBar hp={units[unitId]?.hp ?? MAX_HP} width={GROUP_PORTRAIT} />
          </div>
        ))}
      </div>
      <div
        style={{
          fontSize: 12,
          color: playerColour(owner),
          fontWeight: 600,
          overflowWrap: "anywhere",
        }}
      >
        {ownerName}
      </div>
    </div>
  );
};

export default SelectionPanel;
